import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@repo/error';
import { errorMessage, formatDate, t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import {
  type OrganizationPolicy,
  OrganizationPolicySchema,
  PolicyCategorySchema,
  type PolicyImpact,
  PolicyImpactSchema,
  type PolicyProfile,
} from '@repo/shared/schema';
import { Button } from '@repo/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/card';
import { Form, FormField, FormItem } from '@repo/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select';
import { Switch } from '@repo/ui/switch';
import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { trpc } from '@/providers/trpc';
import { GuardLink, useSetUnsavedChanges } from '@/providers/unsaved-changes';

export const OrganizationPoliciesPage = () => {
  const locale = useLocale();
  const { organizationId = '' } = useParams();
  const [search] = useSearchParams();
  const reportType: 'KYC' | 'KYB' = search.get('type') === 'KYB' ? 'KYB' : 'KYC';
  const input = { organizationId, reportType } as const;
  const query = trpc.organization.getOrganizationPolicy.useQuery(input, {
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    meta: { doNotInvalidateQueryOnMutation: true },
  });
  const history = trpc.organization.listOrganizationPolicyVersions.useQuery(input);
  return (
    <div className="raven-page space-y-6">
      <h1 className="font-semibold text-2xl">{t('policies.title')}</h1>
      <p className="text-muted-foreground">{t('policies.description')}</p>
      <nav aria-label={t('policies.title')} className="flex gap-2">
        {(['KYC', 'KYB'] as const).map((type) => (
          <Button asChild key={type} variant={reportType === type ? 'default' : 'outline'}>
            <GuardLink aria-current={reportType === type ? 'page' : undefined} to={`?type=${type}`}>
              {type}
            </GuardLink>
          </Button>
        ))}
      </nav>
      {query.isLoading && <p>{t('policies.loading')}</p>}
      {query.isError && (
        <p role="alert">
          {t('policies.loadError')}{' '}
          <Button onClick={() => void query.refetch()}>{t('policies.retry')}</Button>
        </p>
      )}
      {query.data && (
        <div className="grid items-start gap-6 xl:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t('policies.active')}</CardTitle>
              <p className="break-all text-sm">
                {query.data.active.source === 'RAVEN_DEFAULT'
                  ? t('policies.defaults')
                  : query.data.active.version}
              </p>
            </CardHeader>
            <CardContent>
              <PolicySettings policy={query.data.active.policy} />
            </CardContent>
          </Card>
          {query.data.canManage && query.data.draft && (
            <PolicyEditor
              active={query.data.active.policy}
              draft={query.data.draft}
              key={`${organizationId}:${reportType}:${query.data.revision}`}
              organizationId={organizationId}
              revision={query.data.revision ?? 0}
            />
          )}
          {query.data.draftUpdatedBy && (
            <p className="text-muted-foreground text-sm">
              {t('policies.draftAuthor')}: {query.data.draftUpdatedBy} ·{' '}
              {query.data.draftUpdatedAt
                ? formatDate(query.data.draftUpdatedAt, locale, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })
                : ''}
            </p>
          )}
        </div>
      )}
      <section className="space-y-3">
        <h2 className="font-semibold text-xl">{t('policies.history')}</h2>
        {history.isError && (
          <p role="alert">
            {t('policies.loadError')}{' '}
            <Button onClick={() => void history.refetch()}>{t('policies.retry')}</Button>
          </p>
        )}
        {history.data?.length === 0 && <p>{t('policies.noHistory')}</p>}
        {history.data?.map((version) => (
          <details className="rounded-lg border p-4" key={version.version}>
            <summary className="cursor-pointer break-all">
              {formatDate(version.publishedAt, locale, { dateStyle: 'medium', timeStyle: 'short' })}{' '}
              · {version.version}
            </summary>
            <p className="my-3 text-sm">
              {t('policies.publisher')}: {version.publishedBy}
            </p>
            <PolicySettings policy={version.policy} />
          </details>
        ))}
      </section>
    </div>
  );
};

const editorSchema = z.object({ policy: OrganizationPolicySchema });
const PolicyEditor = ({
  organizationId,
  draft,
  active,
  revision,
}: {
  organizationId: string;
  draft: OrganizationPolicy;
  active: OrganizationPolicy;
  revision: number;
}) => {
  const form = useForm<z.infer<typeof editorSchema>>({
    resolver: zodResolver(editorSchema, { error: validationError }),
    defaultValues: { policy: draft },
  });
  const { setUnsavedChanges, clearUnsavedChanges } = useSetUnsavedChanges();
  const isDirty = form.formState.isDirty;
  useEffect(() => {
    if (isDirty) {
      setUnsavedChanges({ message: t('policies.unsaved') });
    } else {
      clearUnsavedChanges();
    }
    return clearUnsavedChanges;
  }, [isDirty, setUnsavedChanges, clearUnsavedChanges]);
  const utils = trpc.useUtils();
  const input = { organizationId, reportType: draft.reportType };
  const onError = (cause: unknown) => {
    const error = AppError.parse(cause);
    toast.error(errorMessage(error));
  };
  const save = trpc.organization.updateOrganizationPolicy.useMutation({
    onError,
    meta: { doNotInvalidateQueryOnMutation: true },
  });
  const publish = trpc.organization.publishOrganizationPolicy.useMutation({
    onError,
    meta: { doNotInvalidateQueryOnMutation: true },
  });
  const refresh = async () => {
    await Promise.all([
      utils.organization.getOrganizationPolicy.invalidate(input),
      utils.organization.listOrganizationPolicyVersions.invalidate(input),
    ]);
  };
  const current = form.watch('policy');
  const changes = policyChanges(active, current);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('policies.draft')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(async ({ policy }) => {
              try {
                await save.mutateAsync({ ...input, data: { expectedRevision: revision, policy } });
                form.reset({ policy });
                toast.success(t('policies.saved'));
                await refresh();
              } catch (cause) {
                const error = AppError.parse(cause);
                form.setError('root', { message: errorMessage(error) });
              }
            })}
          >
            <fieldset
              className="space-y-5"
              disabled={save.isPending || publish.isPending || form.formState.isSubmitting}
            >
              <FormField
                control={form.control}
                name="policy"
                render={({ field }) => (
                  <FormItem>
                    <PolicySettings onChange={field.onChange} policy={field.value} />
                    {form.formState.errors.policy && (
                      <p className="text-destructive text-sm" role="alert">
                        {t('policies.minimum')}
                      </p>
                    )}
                  </FormItem>
                )}
              />
              {form.formState.errors.root && (
                <p className="text-destructive text-sm" role="alert">
                  {form.formState.errors.root.message}
                  <Button
                    onClick={() => {
                      form.reset({ policy: draft });
                      void refresh();
                    }}
                    type="button"
                    variant="outline"
                  >
                    {t('policies.discardRefresh')}
                  </Button>
                </p>
              )}
              <Button disabled={!isDirty && revision > 0} type="submit">
                {t('policies.save')}
              </Button>
            </fieldset>
          </form>
        </Form>
        <p className="text-muted-foreground text-sm">{t('policies.publishNote')}</p>
        <h3 className="font-medium">{t('policies.changes')}</h3>
        {changes.length ? (
          <ul className="list-disc space-y-1 ps-5 text-sm">
            {changes.map((change) => (
              <li key={change}>{change}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm">{t('policies.noChanges')}</p>
        )}
        <Button
          disabled={
            isDirty ||
            !revision ||
            save.isPending ||
            publish.isPending ||
            form.formState.isSubmitting
          }
          onClick={async () => {
            try {
              await publish.mutateAsync({ ...input, data: { expectedRevision: revision } });
              toast.success(t('policies.published'));
              await refresh();
            } catch (cause) {
              const error = AppError.parse(cause);
              form.setError('root', { message: errorMessage(error) });
            }
          }}
        >
          {t('policies.publish')}
        </Button>
      </CardContent>
    </Card>
  );
};

export const PolicySettings = ({
  policy,
  onChange,
}: {
  policy: OrganizationPolicy;
  onChange?: (policy: OrganizationPolicy) => void;
}) => (
  <div className="space-y-5">
    {profiles(policy).map(([kind, profile]) => (
      <ProfileSettings
        key={kind}
        kind={kind}
        onChange={onChange ? (value) => onChange({ ...policy, [kind]: value }) : undefined}
        profile={profile}
      />
    ))}
    <p className="text-muted-foreground text-xs">{t('policies.exceptions')}</p>
  </div>
);
const ProfileSettings = ({
  kind,
  profile,
  onChange,
}: {
  kind: ProfileKind;
  profile: PolicyProfile;
  onChange?: (profile: PolicyProfile) => void;
}) => {
  const id = useId();
  return (
    <fieldset className="space-y-3 rounded-md border p-3" disabled={!onChange}>
      <legend className="px-1 font-medium">{t(`policies.${kind}`)}</legend>
      {PolicyCategorySchema.options.map((category) => (
        <div className="flex flex-wrap items-center gap-3" key={category}>
          <Switch
            checked={profile.categories[category].enabled}
            id={`${id}-${category}`}
            onCheckedChange={(enabled) =>
              onChange?.({
                ...profile,
                categories: {
                  ...profile.categories,
                  [category]: { ...profile.categories[category], enabled },
                },
              })
            }
          />
          <label className="min-w-32 flex-1 text-sm" htmlFor={`${id}-${category}`}>
            {t(`policies.${category}`)}
          </label>
          <ImpactSelect
            disabled={!onChange || !profile.categories[category].enabled}
            label={`${t(`policies.${category}`)}: ${t('policies.impact')}`}
            onChange={(impact) =>
              onChange?.({
                ...profile,
                categories: {
                  ...profile.categories,
                  [category]: { ...profile.categories[category], impact },
                },
              })
            }
            value={profile.categories[category].impact}
          />
        </div>
      ))}
      {(['seriousFinancialCrime', 'discrepancy'] as const).map((exception) => (
        <div className="flex flex-wrap items-center gap-3" key={exception}>
          <span className="min-w-32 flex-1 text-sm">{t(`policies.${exception}`)}</span>
          <ImpactSelect
            disabled={!onChange}
            label={t(`policies.${exception}`)}
            onChange={(impact) => onChange?.({ ...profile, [exception]: impact })}
            value={profile[exception]}
          />
        </div>
      ))}
    </fieldset>
  );
};
const ImpactSelect = ({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: PolicyImpact;
  disabled: boolean;
  onChange: (value: PolicyImpact) => void;
}) => (
  <Select
    disabled={disabled}
    onValueChange={(value) => onChange(PolicyImpactSchema.parse(value))}
    value={value}
  >
    <SelectTrigger aria-label={label} className="w-36">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {PolicyImpactSchema.options.map((impact) => (
        <SelectItem key={impact} value={impact}>
          {t(`policies.${impact}`)}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);
type ProfileKind = 'individual' | 'company' | 'member';
const profiles = (policy: OrganizationPolicy): [ProfileKind, PolicyProfile][] =>
  policy.reportType === 'KYC'
    ? [['individual', policy.individual]]
    : [
        ['company', policy.company],
        ['member', policy.member],
      ];
const policyChanges = (active: OrganizationPolicy, draft: OrganizationPolicy): string[] =>
  profiles(draft).flatMap(([kind, profile]) => {
    const original = profiles(active).find(([key]) => key === kind)?.[1];
    if (!original) {
      return [];
    }
    const prefix = t(`policies.${kind}`);
    const changes = PolicyCategorySchema.options.flatMap((category) => {
      const before = original.categories[category];
      const after = profile.categories[category];
      if (before.enabled === after.enabled && before.impact === after.impact) {
        return [];
      }
      const label = (value: typeof before) =>
        `${t(value.enabled ? 'policies.enabled' : 'policies.disabled')} / ${t(`policies.${value.impact}`)}`;
      return [`${prefix} · ${t(`policies.${category}`)}: ${label(before)} → ${label(after)}`];
    });
    for (const exception of ['seriousFinancialCrime', 'discrepancy'] as const) {
      if (original[exception] !== profile[exception]) {
        changes.push(
          `${prefix} · ${t(`policies.${exception}`)}: ${t(`policies.${original[exception]}`)} → ${t(`policies.${profile[exception]}`)}`,
        );
      }
    }
    return changes;
  });
